import { MenuHierarchy } from "@/pages/admin/menuHierarchy/types"
import ConfigService, { Config } from "@shared/helpers/configServices"
import { defineStore } from "pinia"
import { computed, ref, watch } from "vue"

export interface Settings {
  isSidebarMinimized: boolean
  isLoading: boolean
  isSaving: boolean
  isAppLoading?: boolean
  configurations: Config
}

export interface UserPreferences {
  selectedTheme?: string
  menuFavorites?: string
  dashboardWidget?: string
  threeHundredSixty?: string
}

interface LogoMeta {
  key: string
  languageCode: string
  modifiedDate: string
}

const SESSION_KEY = "JTP_global_settings"

export const useGlobalStore = defineStore("global", () => {
  const stored = sessionStorage.getItem(SESSION_KEY)

  const settings = ref<Settings>(
    stored
      ? JSON.parse(stored)
      : {
          isSidebarMinimized: false,
          isLoading: false,
          isSaving: false,
          isAppLoading: true,
        },
  )
  settings.value.configurations = ConfigService.Current

  const userPreferences = ref<UserPreferences>({
    selectedTheme: "light",
    menuFavorites: "",
    threeHundredSixty: "",
  })
  const menuHierarchy = ref<MenuHierarchy[]>([])
  const breadcrumbLeaf = ref<string>("")

  const customLogos = ref<LogoMeta[]>([])

  // Persist to sessionStorage on change
  watch(
    settings,
    (val) => {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(val))
    },
    { deep: true },
  )

  //Getters
  const isSidebarMinimized = computed(() => !!settings.value.isSidebarMinimized)

  //Actions
  function toggleSidebar() {
    settings.value.isSidebarMinimized = !settings.value.isSidebarMinimized
  }

  function setTheme(theme: string) {
    userPreferences.value.selectedTheme = theme
  }

  function setFavorites(favorites: string) {
    if (userPreferences.value.menuFavorites !== favorites) {
      userPreferences.value.menuFavorites = favorites
    }
  }

  function setDashboardWidget(dashboardWidget: string) {
    userPreferences.value.dashboardWidget = dashboardWidget
  }

  function setUserPreferences(preferences: UserPreferences) {
    userPreferences.value = preferences
  }

  function setThreeHundredSixtyPreferences(preferences: string) {
    userPreferences.value.threeHundredSixty = preferences
  }

  function showLoading() {
    settings.value.isLoading = true
  }

  function hideLoading() {
    settings.value.isLoading = false
  }

  function savingStarted() {
    settings.value.isSaving = true
  }

  function savingFinished() {
    settings.value.isSaving = false
  }

  function showAppLoading() {
    settings.value.isAppLoading = true
  }

  function hideAppLoading() {
    settings.value.isAppLoading = false
  }

  function setMenuHierarchy(menu: MenuHierarchy[]) {
    menuHierarchy.value = menu
  }

  function setBreadcrumbLeaf(leaf: string) {
    breadcrumbLeaf.value = leaf
  }

  function setCustomLogos(logos: LogoMeta[]) {
    customLogos.value = logos
  }

  function getCustomLogoUrl(key: string, languageCode: string): string | null {
    const entry = customLogos.value.find((l) => l.key === key && l.languageCode === languageCode)
    if (!entry) return null
    const timestamp = entry.modifiedDate ? encodeURIComponent(entry.modifiedDate) : Date.now().toString()
    const base = ConfigService.Current?.apis?.base ?? ""
    return `${base}/appcustomization/logo/${encodeURIComponent(key)}/${encodeURIComponent(languageCode)}?t=${timestamp}`
  }

  return {
    settings,
    userPreferences,
    setUserPreferences,
    menuHierarchy,
    setMenuHierarchy,
    breadcrumbLeaf,
    setBreadcrumbLeaf,
    setFavorites,
    isSidebarMinimized,
    toggleSidebar,
    setTheme,
    setDashboardWidget,
    showLoading,
    hideLoading,
    showAppLoading,
    hideAppLoading,
    savingStarted,
    savingFinished,
    setThreeHundredSixtyPreferences,
    setCustomLogos,
    getCustomLogoUrl,
  }
})
